/**
 * How the items of one business system account are shown together.
 *
 * The server numbers the items of one account alike (`accountGroup`) and names the account
 * (`accountLabel`); the numbers mean nothing outside the response they came in, so grouping
 * only ever happens within one list. The cart slider groups within each payment and the
 * receipt within each payment card, both from here, so the two cannot drift apart.
 */

/** The little of a cart item the grouping depends on. Structural, to avoid importing the
 *  generated client into a helper its consumers already import it from. */
export interface AccountGroupedItem {
    accountGroup?: number;
    accountLabel?: string;
}

export interface AccountItemGroup<T extends AccountGroupedItem = AccountGroupedItem> {
    /** The server's group number, or null for the items of no account. */
    accountGroup: number | null;

    /** What to head the group with, or null when there is nothing to head it with. */
    label: string | null;

    items: T[];
}

/** The group an account's items gather in, started the first time the account is seen. */
function accountGroupFor<T extends AccountGroupedItem>(
    started: Map<number, AccountItemGroup<T>>,
    accountGroup: number,
    accountLabel: string | undefined
): AccountItemGroup<T> {
    const already = started.get(accountGroup);

    if (already) {
        return already;
    }

    // An empty label is as good as an absent one - there is still nothing to head the group
    // with - so both answer null rather than leaving a caller to head it with nothing.
    const group: AccountItemGroup<T> = {
        accountGroup,
        label: accountLabel || null,
        items: []
    };

    started.set(accountGroup, group);

    return group;
}

/**
 * The items gathered by account, accounts in the order they first appear, followed by one
 * unlabelled group holding the items of no account, if there are any.
 */
export function groupItemsByAccount<T extends AccountGroupedItem>(
    items: readonly T[] | null | undefined
): AccountItemGroup<T>[] {
    const byAccount = new Map<number, AccountItemGroup<T>>();
    const ungrouped: AccountItemGroup<T> = {
        accountGroup: null,
        label: null,
        items: []
    };

    for (const item of items ?? []) {
        // Groups are numbered from one, so no group means the business system attributed the
        // item to no account.
        const group = item.accountGroup
            ? accountGroupFor(byAccount, item.accountGroup, item.accountLabel)
            : ungrouped;

        group.items.push(item);
    }

    const groups = Array.from(byAccount.values());

    return ungrouped.items.length ? [...groups, ungrouped] : groups;
}

/**
 * Whether the groups are worth heading. One group - one account, or no accounts - reads as
 * the flat list it always was; a heading over it would say nothing.
 */
export function hasAccountGroupHeadings(groups: readonly AccountItemGroup[]): boolean {
    return groups.length > 1;
}
